import {
  type BoardRuntimeConfig,
  getBoardPublicPath,
} from "@/config/boardRuntimeConfig";

const STATIC_ROUTE_PATHS: ReadonlySet<string> = new Set([
  "/",
  "/api-docs",
  "/app-auth",
  "/archive",
  "/board",
  "/jukebox",
  "/search",
  "/settings",
  "/announcements",
]);

const SINGLE_PARAMETER_ROUTE_PATTERNS: readonly RegExp[] = [
  /^\/announcements\/[^/]+$/u,
  /^\/history\/[^/]+$/u,
  /^\/space\/[^/]+$/u,
  /^\/thread\/[^/]+$/u,
];

/**
 * Resolve a basepath-relative TanStack Router path to its public canonical path.
 * Keep this allowlist aligned with the files under `src/routes`; assets are
 * intentionally outside this contract.
 */
export function resolveFrontendRouteCanonicalPath(
  config: BoardRuntimeConfig,
  routePath: string,
): string | null {
  const normalizedPath = normalizeRoutePath(routePath);
  if (normalizedPath === null) return null;
  if (config.mix !== undefined && /^\/thread\/[^/]+$/u.test(normalizedPath)) {
    return null;
  }

  if (
    STATIC_ROUTE_PATHS.has(normalizedPath) ||
    SINGLE_PARAMETER_ROUTE_PATTERNS.some((pattern) =>
      pattern.test(normalizedPath),
    )
  ) {
    return getBoardPublicPath(config, normalizedPath);
  }

  const mixThread = /^\/([^/]+)\/thread\/([^/]+)$/u.exec(normalizedPath);
  if (mixThread === null || config.mix === undefined) return null;
  const [, encodedSlug, threadId] = mixThread;
  if (encodedSlug === undefined || threadId === undefined) return null;

  let slug: string;
  try {
    slug = decodeURIComponent(encodedSlug);
  } catch {
    return null;
  }
  if (!config.mix.registeredSlugs.includes(slug)) return null;
  return `/b/${encodedSlug}/thread/${threadId}`;
}

/** Resolve a browser pathname, including the deployment basepath, to canonical. */
export function resolveMountedFrontendCanonicalPath(
  config: BoardRuntimeConfig,
  pathname: string,
): string | null {
  const routePath = stripPublicBasePath(config.publicBasePath, pathname);
  return routePath === null
    ? null
    : resolveFrontendRouteCanonicalPath(config, routePath);
}

function stripPublicBasePath(
  publicBasePath: string,
  pathname: string,
): string | null {
  if (publicBasePath === "") return pathname;
  if (pathname === publicBasePath) return "/";
  if (!pathname.startsWith(`${publicBasePath}/`)) return null;
  return pathname.slice(publicBasePath.length);
}

function normalizeRoutePath(routePath: string): string | null {
  if (!routePath.startsWith("/") || routePath.includes("//")) return null;
  return routePath.length > 1 && routePath.endsWith("/")
    ? routePath.slice(0, -1)
    : routePath;
}
